/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { isError } from "@epic-mw/error-types";
import useSWRImmutable from "swr/immutable";

import { useApiConfig } from "../api-config";
import { getApiPath } from "../utils";

interface IndustrySaveRes {
	success?: boolean;
	error?: string;
}

export interface IndustrySaveReq {
	industry_v1?: string | null;
	industry_v2?: string | null;
}

const saveIndustry = async (req: IndustrySaveReq, serverUrl?: string): Promise<IndustrySaveRes> => {
	try {
		const response = await fetch(`${getApiPath(serverUrl)}/data-router`, {
			method: "POST",
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
			},
			body: JSON.stringify(req),
		});
		const json: IndustrySaveRes = await response.json();
		return json;
	} catch (err) {
		console.error("saveIndustry() failed to load", isError(err) && err.message);
		throw err;
	}
};

interface HookRes {
	saveSuccess: boolean;
	isLoading: boolean;
	error: any;
}

export const useSaveIndustry = (req: IndustrySaveReq | null, execute: boolean): HookRes => {
	const { serverUrl } = useApiConfig();
	const shouldFetch = Boolean(req?.industry_v1 && req?.industry_v2 && execute);

	const swrKey = shouldFetch ? JSON.stringify([req, serverUrl]) : null;
	const { data, error } = useSWRImmutable(swrKey, () => saveIndustry(req!, serverUrl), { errorRetryCount: 0 });

	return {
		saveSuccess: data?.success === true,
		isLoading: Boolean(!data && !error && shouldFetch),
		error: error || data?.success === false,
	};
};
